'use memo';

import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { HeaderTabs } from '@/components/Navigation/HeaderTabs';
import type { TabWithIndicatorProps } from '@/components/Navigation/HeaderTabs';
import { Text } from '@/components/Typography/Text';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
import { useSuspenseQueryOperationPackageInstance } from './-queries';
export const Route = createFileRoute('/_stacktrace/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_layout')({
  component: RouteComponent
});

/**
 * QA オペレーションコア インスタンス詳細のレイアウト。
 *
 * テスト計画作成（TestPlanCreation）は単一フォームタスクのため「詳細」のみの構成。
 * テスト実施（TestExecution）・テストアサイン（TestAssignment）はデータタブ
 * 遂行型のため「詳細 / データ」2 タブを持つ（PN「リリースプラン作成」の
 * `_layout.tsx` と同型）。packageType でタブ表示を切り替え、同一ルートで
 * 各パッケージ種別を扱う。共通ラベル（戻る / タブ名）は testExecution 配下の
 * 既存キーを共用する。
 */
function RouteComponent() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'qaOperationsCore.testExecution.instance'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const hasDataTab = instance.packageType === 'TestExecution' || instance.packageType === 'TestAssignment' || instance.packageType === 'IssueReconciliation';
  const tabs = [{
    to: '/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
    params,
    children: t('tabs.detail'),
    activeOptions: {
      exact: true
    }
  }, {
    to: '/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/data',
    params,
    children: t('tabs.data'),
    activeOptions: {
      exact: true
    }
  }] satisfies TabWithIndicatorProps[];
  return <div className="flex flex-col h-full">
      <VStack className="bg-neutral-fill px-12 pt-4 gap-3">
        <Link to="/qa-operations-core">
          <Icon iconName="ChevronRight" text={t('backToCore')} variant="small" right />
        </Link>
        <Text variant="h4">{instance.name}</Text>
        {hasDataTab && <HStack>
            <HeaderTabs tabs={tabs} aria-label={t('tabs.ariaLabel')} />
          </HStack>}
      </VStack>
      <Outlet />
    </div>;
}